import { useEffect, useMemo, useState } from "react";
import {
  Info, TruckTrailer, MagnifyingGlass, ShieldCheck, EnvelopeSimple,
  BellRinging, ClipboardText, HardDrives, Cloud, Truck, RocketLaunch,
  Heartbeat, Clock, Copy, Warning, CheckCircle, XCircle, Question,
  Eye, UserFocus, SquaresFour, AppWindow,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * System Diagnostics — Version 1.1 permanent operational tool
 * (Administration → System Diagnostics).
 *
 * Left-rail module selector + right pane rendering the active module.
 * Every module is a read-only probe: nothing here mutates data. Modules
 * live inline so a new one can be added by dropping a { key, label, icon,
 * component } object into MODULES.
 *
 * Governance: ARCH-DIAG (read-only), DSG-001 (semantic tokens), and
 * Administrator/Owner access enforced by both the calling backend
 * endpoints and the parent Administration tab visibility.
 */
export default function SystemDiagnosticsPanel({ initialModule = "system-info" }) {
  const [active, setActive] = useState(initialModule);

  return (
    <div data-testid="admin-panel-diagnostics">
      <div className="mb-5 border-b border-slate-200 pb-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">System Diagnostics</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Read-only operational checks. Every module runs against the caller&apos;s
          own authenticated session so administrators can reproduce user reports
          without needing shell or log access.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <nav
          data-testid="diagnostics-modules-nav"
          className="col-span-12 md:col-span-3 lg:col-span-3"
        >
          <ul className="space-y-1">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const isActive = active === m.key;
              const isComingSoon = m.comingSoon;
              return (
                <li key={m.key}>
                  <button
                    data-testid={`diag-module-${m.key}`}
                    onClick={() => setActive(m.key)}
                    className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    <Icon size={14} weight="bold" />
                    <span className="flex-1 truncate">{m.label}</span>
                    {isComingSoon && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                          isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        Soon
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-[11px] text-slate-400">
            Read-only. Administrator/Owner access. Modules can be added by
            registering an endpoint + a client component.
          </p>
        </nav>

        <section
          data-testid="diagnostics-active-module"
          className="col-span-12 md:col-span-9 lg:col-span-9"
        >
          {(() => {
            const mod = MODULES.find((m) => m.key === active);
            if (!mod) return null;
            const Comp = mod.component;
            return <Comp module={mod} />;
          })()}
        </section>
      </div>
    </div>
  );
}


// ==========================================================================
// System Information module
// ==========================================================================
function SystemInfoModule({ module: mod }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await api.get("/admin/diagnostics/system");
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({
        loading: false,
        data: null,
        error: e?.response?.data?.detail || e?.message || "Could not load system information",
      });
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <ModuleShell mod={mod} onReload={load}>
      {state.loading && (
        <div className="py-6 text-center text-sm text-slate-500">Loading system information…</div>
      )}
      {state.error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {state.error}
        </div>
      )}
      {state.data && (
        <>
          <SectionCard title="Dispatch Platform">
            <MetaGrid>
              <MetaLine label="Version" value={state.data.app.version} mono />
              <MetaLine label="Build" value={state.data.app.build} mono />
              <MetaLine label="Git Commit" value={state.data.app.git_commit || "—"} mono />
              <MetaLine label="Environment" value={state.data.app.environment}>
                <EnvironmentTag env={state.data.app.environment} />
              </MetaLine>
              <MetaLine label="Started At" value={formatIso(state.data.app.started_at_utc)} />
              <MetaLine label="Server Time" value={formatIso(state.data.app.server_time_utc)} />
              <MetaLine label="Python" value={state.data.app.python_version} mono />
              <MetaLine label="Platform" value={state.data.app.platform} mono muted />
            </MetaGrid>
          </SectionCard>

          <SectionCard title="External Services">
            <ul className="divide-y divide-slate-100">
              {Object.entries(state.data.services).map(([key, svc]) => (
                <ServiceRow key={key} name={SERVICE_LABELS[key] || key} svc={svc} />
              ))}
            </ul>
          </SectionCard>

          <p className="mt-3 text-[11px] text-slate-400">
            When someone reports an issue, capture this view — the first
            question is always &quot;what build are you on?&quot;.
          </p>
        </>
      )}
    </ModuleShell>
  );
}

const SERVICE_LABELS = {
  database: "Database (MongoDB)",
  microsoft_entra: "Microsoft Entra Authentication",
  microsoft_graph: "Microsoft Graph Email",
  samsara: "Samsara Fleet",
  object_storage: "Object Storage",
};

function ServiceRow({ name, svc }) {
  const { icon, tone, label } = renderStatus(svc.status);
  return (
    <li className="flex items-start justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900">{name}</div>
        {svc.detail && (
          <div className="mt-0.5 truncate font-mono text-[11px] text-slate-500" title={svc.detail}>
            {svc.detail}
          </div>
        )}
      </div>
      <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
        {icon}
        {label}
      </div>
    </li>
  );
}

function renderStatus(status) {
  switch (status) {
    case "connected":
      return { icon: <CheckCircle size={12} weight="fill" />, tone: "bg-emerald-50 text-emerald-700", label: "Connected" };
    case "configured":
      return { icon: <CheckCircle size={12} weight="fill" />, tone: "bg-sky-50 text-sky-700", label: "Configured" };
    case "not_configured":
      return { icon: <Question size={12} weight="fill" />, tone: "bg-slate-100 text-slate-600", label: "Not configured" };
    case "error":
      return { icon: <XCircle size={12} weight="fill" />, tone: "bg-red-50 text-red-700", label: "Error" };
    default:
      return { icon: <Warning size={12} weight="fill" />, tone: "bg-amber-50 text-amber-700", label: status };
  }
}

function EnvironmentTag({ env }) {
  const tone =
    env === "Production" ? "bg-emerald-50 text-emerald-700" :
    env === "Preview"    ? "bg-sky-50 text-sky-700" :
                            "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {env}
    </span>
  );
}


// ==========================================================================
// Delivery Profile module (rpt_004)
// ==========================================================================
function DeliveryProfileDiagModule({ module: mod }) {
  const [account, setAccount] = useState("8133112");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);

  const run = async () => {
    if (!account.trim()) {
      toast.error("Enter an account number first");
      return;
    }
    setRunning(true);
    setResult(null);
    setError(null);
    setStatus(null);
    setMe(null);
    try {
      const meResp = await api.get("/auth/me");
      setMe(meResp.data);
    } catch (e) {
      setMe({ error: e?.response?.data?.detail || String(e), http_status: e?.response?.status });
    }
    try {
      const resp = await api.get(
        `/delivery-profiles/_diag/${encodeURIComponent(account.trim())}`,
      );
      setStatus(resp.status);
      setResult(resp.data);
    } catch (e) {
      setStatus(e?.response?.status || null);
      setError({
        message: e?.message,
        detail: e?.response?.data?.detail,
        body: e?.response?.data,
      });
    } finally {
      setRunning(false);
    }
  };

  const copyAll = async () => {
    const payload = {
      captured_at_client_utc: new Date().toISOString(),
      caller_context: me,
      diag_http_status: status,
      diag_body: result,
      diag_error: error,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast.success("Diagnostic payload copied to clipboard");
    } catch {
      toast.error("Could not access clipboard — select the JSON manually");
    }
  };

  return (
    <ModuleShell mod={mod}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Account Number
          </Label>
          <Input
            data-testid="diagnostics-account"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            placeholder="8133112"
            className="font-mono"
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          />
        </div>
        <Button
          data-testid="diagnostics-run"
          onClick={run}
          disabled={running}
          className="bg-[#1A1A1A] hover:bg-[#1A1A1A]"
        >
          <MagnifyingGlass size={14} weight="bold" className="mr-1" />
          {running ? "Running…" : "Run Diagnostic"}
        </Button>
      </div>

      {me && (
        <SectionCard title="Your resolved session (from /auth/me)" spacedTop>
          {me.error ? (
            <div className="flex items-start gap-1.5 text-sm text-amber-800">
              <Warning size={14} weight="bold" className="mt-0.5" />
              <span>/auth/me returned {me.http_status || "an error"}: {me.error}</span>
            </div>
          ) : (
            <MetaGrid>
              <MetaLine label="Email" value={me.email} mono />
              <MetaLine label="Role" value={me.role} />
              <MetaLine label="Full Name" value={me.full_name} />
              <MetaLine label="Status" value={me.status} />
            </MetaGrid>
          )}
        </SectionCard>
      )}

      {(status !== null || error) && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono font-semibold text-slate-700">
            HTTP {status ?? "—"}
          </span>
          {status === 200 && (
            <span className="text-emerald-700">Backend returned a diagnostic body.</span>
          )}
          {status === 401 && <span className="text-amber-800">Not signed in</span>}
          {status === 403 && <span className="text-amber-800">Insufficient role</span>}
          {status === 404 && (
            <span className="text-amber-800">
              Endpoint missing — this build predates the diagnostic. Redeploy required.
            </span>
          )}
        </div>
      )}

      {(result || error) && (
        <div className="mt-3">
          <div className="mb-1.5 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Response JSON
            </div>
            <button
              data-testid="diagnostics-copy"
              onClick={copyAll}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Copy size={12} weight="bold" /> Copy full payload
            </button>
          </div>
          <pre
            data-testid="diagnostics-json"
            className="max-h-[420px] overflow-auto rounded-md border border-slate-200 bg-slate-950 p-3 font-mono text-[12px] leading-relaxed text-slate-100"
          >
{JSON.stringify(result ?? error, null, 2)}
          </pre>
        </div>
      )}

      {!result && !error && !running && (
        <p className="mt-4 text-xs text-slate-400">
          Enter an account number and press Run. Copy the resulting JSON and
          paste it into the support ticket so we can compare it byte-for-byte
          with another user&apos;s session.
        </p>
      )}
    </ModuleShell>
  );
}


// ==========================================================================
// Permission Audit Suite
// ==========================================================================
// Governance: ARCH-DIAG · permanent System Diagnostics module.
//
// Wraps four sibling audits:
//   • By User        — what will this user see when they sign in?
//   • Compare Users  — where do two users' experiences differ?
//   • By Role        — what does this role structurally grant, and which
//                       users hold it?
//   • By Application — who exactly can see this application today?
//
// The user directory is loaded once at the suite level and passed to each
// tab so switching tabs never re-fetches. All audit endpoints share the
// same _build_visibility_payload helper on the backend, so results are
// byte-exact to the live runtime — no matter which lens you're using.
function PermissionAuditSuite({ module: mod }) {
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("by-user");
  const [usersError, setUsersError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/diagnostics/visibility/users");
        if (!alive) return;
        setUsers(r.data);
      } catch (e) {
        if (alive) setUsersError(e?.response?.data?.detail || "Could not load users");
      }
    })();
    return () => { alive = false; };
  }, []);

  const TABS = [
    { key: "by-user", label: "By User", icon: UserFocus },
    { key: "compare", label: "Compare Users", icon: Eye },
    { key: "by-role", label: "By Role", icon: SquaresFour },
    { key: "by-application", label: "By Application", icon: AppWindow },
  ];

  return (
    <ModuleShell mod={mod}>
      {/* Tab bar --------------------------------------------------------- */}
      <div
        data-testid="permission-audit-tabs"
        className="mb-4 flex flex-wrap gap-1 border-b border-slate-200"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`permission-audit-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition ${
                active
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon size={13} weight="bold" />
              {t.label}
            </button>
          );
        })}
      </div>

      {usersError && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {usersError}
        </div>
      )}

      {tab === "by-user" && <ByUserTab users={users} />}
      {tab === "compare" && <CompareUsersTab users={users} />}
      {tab === "by-role" && <ByRoleTab users={users} />}
      {tab === "by-application" && <ByApplicationTab />}
    </ModuleShell>
  );
}


// ==========================================================================
// Tab 1 · By User (formerly the standalone Application Visibility Audit)
// ==========================================================================
// Pre-flight validation before onboarding. Given any user, show exactly what
// they will see on Dashboard and My Applications — before their first login.
// The Preview As User dialog renders a simulation of the caller's Dashboard
// AS IF they were the selected user — no session switch, no auth swap.
function ByUserTab({ users }) {
  const [userId, setUserId] = useState("");
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);

  // Auto-select Owner (Kevin) on first load.
  useEffect(() => {
    if (!userId && users.length) {
      const owner = users.find((u) => u.role === "Owner");
      setUserId((owner || users[0] || {}).id || "");
    }
  }, [users, userId]);

  const run = async () => {
    if (!userId) {
      toast.error("Select a user first");
      return;
    }
    setLoading(true);
    setError(null);
    setAudit(null);
    try {
      const r = await api.get(`/admin/diagnostics/visibility/${userId}`);
      setAudit(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) run();
  }, [userId]);

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            User
          </Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="visibility-audit-user"><SelectValue placeholder="Select user…" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name || u.email} · {u.role}
                  {u.status !== "Active" ? ` · ${u.status}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="visibility-audit-preview-btn"
          variant="outline"
          onClick={() => setPreview(true)}
          disabled={!audit}
          className="border-slate-300"
        >
          <Eye size={14} weight="bold" className="mr-1" /> Preview As User
        </Button>
      </div>

      {loading && (
        <div className="mt-6 py-6 text-center text-sm text-slate-500">Auditing…</div>
      )}
      {error && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {audit && !loading && (
        <>
          <SectionCard title="User Information" spacedTop>
            <MetaGrid>
              <MetaLine label="Name" value={audit.user.full_name} />
              <MetaLine label="Email" value={audit.user.email} mono />
              <MetaLine label="Role" value={audit.user.role} />
              <MetaLine label="Department">
                {audit.user.department_roles.length
                  ? audit.user.department_roles.join(", ")
                  : <span className="text-slate-400">Not set</span>}
              </MetaLine>
              <MetaLine label="Status">
                <StatusPill value={audit.user.status} />
              </MetaLine>
              <MetaLine label="Generated" value={formatIso(audit.generated_at_utc)} muted />
            </MetaGrid>
          </SectionCard>

          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
            <SummaryTile
              label="Widgets"
              value={audit.summary.widgets_granted}
              suffix={`of ${audit.summary.widgets_total}`}
              tone="emerald"
              testid="audit-summary-widgets"
            />
            <SummaryTile
              label="Accessible Apps"
              value={audit.summary.applications_granted}
              suffix={`of ${audit.summary.applications_total}`}
              tone="emerald"
              testid="audit-summary-apps-granted"
            />
            <SummaryTile
              label="Hidden Apps"
              value={audit.summary.applications_hidden}
              suffix={`of ${audit.summary.applications_total}`}
              tone={audit.summary.applications_hidden > 0 ? "slate" : "emerald"}
              testid="audit-summary-apps-hidden"
            />
            <SummaryTile
              label="Widgets Denied"
              value={audit.summary.widgets_denied}
              suffix={`of ${audit.summary.widgets_total}`}
              tone={audit.summary.widgets_denied > 0 ? "slate" : "emerald"}
              testid="audit-summary-widgets-denied"
            />
          </div>

          <SectionCard title="Dashboard Widgets" spacedTop>
            <div className="divide-y divide-slate-100">
              {audit.widgets.map((w) => (
                <VisibilityRow
                  key={w.key}
                  testid={`audit-widget-${w.key}`}
                  granted={w.granted}
                  primary={w.title}
                  secondary={`Slot · ${w.slot}`}
                  reason={w.reason}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Application Registry" spacedTop>
            {audit.applications.length === 0 ? (
              <div className="py-3 text-center text-sm text-slate-500">
                No applications registered.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {audit.applications.map((a) => (
                  <VisibilityRow
                    key={a.id}
                    testid={`audit-app-${a.id}`}
                    granted={a.granted}
                    primary={a.name}
                    secondary={
                      <span className="flex flex-wrap items-center gap-1.5">
                        <ChipMuted>{a.status}</ChipMuted>
                        {a.deployment && <ChipMuted>{a.deployment}</ChipMuted>}
                        <ChipMuted>{a.launch_method}</ChipMuted>
                      </span>
                    }
                    reason={a.reason}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <p className="mt-3 text-[11px] text-slate-400">
            Uses the same permission resolver as the live runtime — audit results
            are byte-exact to what the user will see on sign-in.
          </p>
        </>
      )}

      {preview && audit && (
        <PreviewAsUserDialog audit={audit} onClose={() => setPreview(false)} />
      )}
    </>
  );
}


// ==========================================================================
// Tab 2 · Compare Two Users
// ==========================================================================
// Onboarding companion: verify a newly provisioned employee has the same
// operational experience as another employee in the same role. The core UX
// is a "Show Differences Only" toggle so administrators can scroll straight
// to the rows that need attention.
function CompareUsersTab({ users }) {
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [diffsOnly, setDiffsOnly] = useState(false);

  const run = async () => {
    if (!aId || !bId) return;
    if (aId === bId) {
      toast.error("Choose two different users to compare");
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const r = await api.get(`/admin/diagnostics/visibility/compare/${aId}/${bId}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load comparison");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aId && bId && aId !== bId) run();
  }, [aId, bId]);

  const widgetRows = useMemo(() => {
    if (!data) return [];
    return diffsOnly ? data.widgets.filter((w) => w.differs) : data.widgets;
  }, [data, diffsOnly]);
  const appRows = useMemo(() => {
    if (!data) return [];
    return diffsOnly ? data.applications.filter((a) => a.differs) : data.applications;
  }, [data, diffsOnly]);

  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            User A
          </Label>
          <Select value={aId} onValueChange={setAId}>
            <SelectTrigger data-testid="compare-user-a"><SelectValue placeholder="Select user A…" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name || u.email} · {u.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            User B
          </Label>
          <Select value={bId} onValueChange={setBId}>
            <SelectTrigger data-testid="compare-user-b"><SelectValue placeholder="Select user B…" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name || u.email} · {u.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="mt-4 py-6 text-center text-sm text-slate-500">Comparing…</div>
      )}
      {error && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="grid grid-cols-2 gap-2">
              <UserHeaderPill user={data.user_a} label="A" />
              <UserHeaderPill user={data.user_b} label="B" />
            </div>
            <label
              data-testid="compare-diffs-only-toggle"
              className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700"
            >
              <input
                type="checkbox"
                checked={diffsOnly}
                onChange={(e) => setDiffsOnly(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-400"
              />
              Show Differences Only
            </label>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            <SummaryTile
              label="Widgets"
              value={data.summary.widgets_identical}
              suffix={`of ${data.summary.widgets_total} match`}
              tone={data.summary.widgets_differ === 0 ? "emerald" : "slate"}
              testid="compare-summary-widgets"
            />
            <SummaryTile
              label="Widget Diffs"
              value={data.summary.widgets_differ}
              suffix={`of ${data.summary.widgets_total}`}
              tone={data.summary.widgets_differ === 0 ? "emerald" : "slate"}
              testid="compare-summary-widget-diffs"
            />
            <SummaryTile
              label="Apps"
              value={data.summary.applications_identical}
              suffix={`of ${data.summary.applications_total} match`}
              tone={data.summary.applications_differ === 0 ? "emerald" : "slate"}
              testid="compare-summary-apps"
            />
            <SummaryTile
              label="App Diffs"
              value={data.summary.applications_differ}
              suffix={`of ${data.summary.applications_total}`}
              tone={data.summary.applications_differ === 0 ? "emerald" : "slate"}
              testid="compare-summary-app-diffs"
            />
          </div>

          <SectionCard title="Dashboard Widgets" spacedTop>
            {widgetRows.length === 0 ? (
              <div className="py-3 text-center text-sm text-slate-500">
                {diffsOnly ? "No widget differences." : "No widgets."}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {widgetRows.map((w) => (
                  <CompareRow
                    key={w.key}
                    testid={`compare-widget-${w.key}`}
                    primary={w.title}
                    secondary={`Slot · ${w.slot}`}
                    a={{ granted: w.a_granted, reason: w.a_reason }}
                    b={{ granted: w.b_granted, reason: w.b_reason }}
                    differs={w.differs}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Application Registry" spacedTop>
            {appRows.length === 0 ? (
              <div className="py-3 text-center text-sm text-slate-500">
                {diffsOnly ? "No application differences." : "No applications."}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {appRows.map((a) => (
                  <CompareRow
                    key={a.id}
                    testid={`compare-app-${a.id}`}
                    primary={a.name}
                    secondary={
                      <span className="flex flex-wrap items-center gap-1.5">
                        <ChipMuted>{a.status}</ChipMuted>
                        {a.deployment && <ChipMuted>{a.deployment}</ChipMuted>}
                        <ChipMuted>{a.launch_method}</ChipMuted>
                      </span>
                    }
                    a={{ granted: a.a_granted, reason: a.a_reason }}
                    b={{ granted: a.b_granted, reason: a.b_reason }}
                    differs={a.differs}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}

      {!data && !loading && !error && (
        <p className="mt-6 text-center text-xs text-slate-400">
          Pick two users to compare their Dashboard widgets and Application
          Registry visibility side-by-side.
        </p>
      )}
    </>
  );
}

function UserHeaderPill({ user, label }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5">
      <span className="rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
        {label}
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-slate-900">{user.full_name}</div>
        <div className="truncate font-mono text-[10px] text-slate-500">{user.role}</div>
      </div>
    </div>
  );
}

function CompareRow({ primary, secondary, a, b, differs, testid }) {
  return (
    <div
      data-testid={testid}
      data-differs={differs ? "true" : "false"}
      className={`grid grid-cols-1 gap-2 py-2.5 md:grid-cols-[1fr_auto_auto] md:items-start ${
        differs ? "bg-amber-50/40" : ""
      }`}
    >
      <div>
        <div className="text-sm font-medium text-slate-900">{primary}</div>
        {secondary && <div className="mt-0.5 text-[11px] text-slate-500">{secondary}</div>}
        {differs && (
          <span className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
            Differs
          </span>
        )}
      </div>
      <CompareSlot label="A" granted={a.granted} reason={a.reason} />
      <CompareSlot label="B" granted={b.granted} reason={b.reason} />
    </div>
  );
}

function CompareSlot({ label, granted, reason }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 md:min-w-[180px]">
      <div className="flex items-center gap-1.5">
        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
          {label}
        </span>
        {granted ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
            <CheckCircle size={12} weight="fill" /> Granted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">
            <XCircle size={12} weight="fill" /> Denied
          </span>
        )}
      </div>
      <div className="mt-1 text-[11px] text-slate-600">{reason}</div>
    </div>
  );
}


// ==========================================================================
// Tab 3 · By Role
// ==========================================================================
// Two audiences: (1) what does a role structurally grant across widgets and
// applications, and (2) which users currently hold that role. Answers "we
// expected every Owner to see BMO but only Kevin has it" at a glance.
const AUDITABLE_ROLES = ["Administrator", "Owner", "Office Staff", "Driver"];

function ByRoleTab({ users }) {
  const [role, setRole] = useState("Owner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const r = await api.get(`/admin/diagnostics/visibility/role/${encodeURIComponent(role)}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load role audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); }, [role]);

  // Distinct roles present in the seeded users, ordered predictably.
  const availableRoles = useMemo(() => {
    const seen = new Set(users.map((u) => u.role).filter(Boolean));
    return AUDITABLE_ROLES.filter((r) => seen.has(r)).concat(
      Array.from(seen).filter((r) => !AUDITABLE_ROLES.includes(r)),
    );
  }, [users]);

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Role
          </Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="role-audit-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {availableRoles.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="mt-4 py-6 text-center text-sm text-slate-500">Auditing role…</div>
      )}
      {error && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
            <SummaryTile
              label="Widgets Granted"
              value={data.summary.widgets_granted}
              suffix={`of ${data.summary.widgets_total}`}
              tone="emerald"
              testid="role-summary-widgets"
            />
            <SummaryTile
              label="Apps via Role"
              value={data.summary.applications_granted_via_role}
              suffix={`of ${data.summary.applications_total}`}
              tone="emerald"
              testid="role-summary-apps"
            />
            <SummaryTile
              label="Users Holding"
              value={data.summary.users_holding_role}
              suffix="active + inactive"
              tone="slate"
              testid="role-summary-users"
            />
            <SummaryTile
              label="Role"
              value={data.role}
              tone="slate"
              testid="role-summary-name"
            />
          </div>

          <SectionCard title="Dashboard Widgets" spacedTop>
            <div className="divide-y divide-slate-100">
              {data.widgets.map((w) => (
                <VisibilityRow
                  key={w.key}
                  testid={`role-widget-${w.key}`}
                  granted={w.granted}
                  primary={w.title}
                  secondary={`Slot · ${w.slot}`}
                  reason={w.reason}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Application Registry — via role only" spacedTop>
            {data.applications.length === 0 ? (
              <div className="py-3 text-center text-sm text-slate-500">
                No applications registered.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.applications.map((a) => (
                  <VisibilityRow
                    key={a.id}
                    testid={`role-app-${a.id}`}
                    granted={a.granted_via_role}
                    primary={a.name}
                    secondary={
                      <span className="flex flex-wrap items-center gap-1.5">
                        <ChipMuted>{a.status}</ChipMuted>
                        {a.deployment && <ChipMuted>{a.deployment}</ChipMuted>}
                        {a.permitted_emails_count > 0 && (
                          <ChipMuted>+ {a.permitted_emails_count} user-scoped</ChipMuted>
                        )}
                      </span>
                    }
                    reason={a.reason}
                  />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Users holding this role" spacedTop>
            {data.users.length === 0 ? (
              <div className="py-3 text-center text-sm text-slate-500">
                No users currently hold this role.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.users.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`role-user-${u.id}`}
                    className="flex flex-wrap items-center gap-2 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900">{u.full_name}</div>
                      <div className="truncate font-mono text-[11px] text-slate-500">{u.email}</div>
                    </div>
                    <StatusPill value={u.status} />
                    <ChipMuted>Widgets {u.widgets_granted}/{u.widgets_total}</ChipMuted>
                    <ChipMuted>Apps {u.applications_granted}/{u.applications_total}</ChipMuted>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}
    </>
  );
}


// ==========================================================================
// Tab 4 · By Application
// ==========================================================================
// Reverse audit: pick an application, see every user × grant/deny + reason.
// Answers "who exactly can see BMO Online Banking today?" without eyeballing
// permitted_emails arrays.
function ByApplicationTab() {
  const [apps, setApps] = useState([]);
  const [appId, setAppId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load applications directory once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/applications");
        if (!alive) return;
        setApps(r.data);
        if (r.data.length && !appId) setAppId(r.data[0].id);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load applications");
      }
    })();
    return () => { alive = false; };
  }, []);

  const run = async () => {
    if (!appId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const r = await api.get(`/admin/diagnostics/visibility/application/${appId}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load application audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (appId) run(); }, [appId]);

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Application
          </Label>
          <Select value={appId} onValueChange={setAppId}>
            <SelectTrigger data-testid="app-audit-application"><SelectValue placeholder="Select application…" /></SelectTrigger>
            <SelectContent>
              {apps.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} · {a.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="mt-4 py-6 text-center text-sm text-slate-500">Auditing application…</div>
      )}
      {error && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <SectionCard title="Application" spacedTop>
            <MetaGrid>
              <MetaLine label="Name" value={data.application.name} />
              <MetaLine label="Type" value={data.application.app_type} />
              <MetaLine label="Status">
                <ChipMuted>{data.application.status}</ChipMuted>
              </MetaLine>
              <MetaLine label="Deployment">
                {data.application.deployment ? <ChipMuted>{data.application.deployment}</ChipMuted> : "—"}
              </MetaLine>
              <MetaLine label="Launch Method" value={data.application.launch_method} mono />
              <MetaLine label="Owner" value={data.application.owner_name || "—"} />
              <MetaLine label="Explicit users" value={data.application.permitted_emails.length} mono />
              <MetaLine label="Permitted roles">
                {data.application.permitted_roles.length
                  ? data.application.permitted_roles.map((r) => (
                      <ChipMuted key={r}>{r}</ChipMuted>
                    ))
                  : <span className="text-slate-400">None</span>}
              </MetaLine>
            </MetaGrid>
          </SectionCard>

          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3">
            <SummaryTile
              label="Users Granted"
              value={data.summary.users_granted}
              suffix={`of ${data.summary.users_total}`}
              tone="emerald"
              testid="app-summary-granted"
            />
            <SummaryTile
              label="Users Denied"
              value={data.summary.users_denied}
              suffix={`of ${data.summary.users_total}`}
              tone={data.summary.users_denied > 0 ? "slate" : "emerald"}
              testid="app-summary-denied"
            />
            <SummaryTile
              label="Users Total"
              value={data.summary.users_total}
              tone="slate"
              testid="app-summary-total"
            />
          </div>

          <SectionCard title="Users" spacedTop>
            <div className="divide-y divide-slate-100">
              {data.users.map((u) => (
                <VisibilityRow
                  key={u.id}
                  testid={`app-user-${u.id}`}
                  granted={u.granted}
                  primary={u.full_name}
                  secondary={
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-[11px] text-slate-500">{u.email}</span>
                      <ChipMuted>{u.role}</ChipMuted>
                      {u.status !== "Active" && <ChipMuted>{u.status}</ChipMuted>}
                    </span>
                  }
                  reason={u.reason}
                />
              ))}
            </div>
          </SectionCard>

          <p className="mt-3 text-[11px] text-slate-400">
            Reverse view of the By-User audit. Use this after any change to an
            application&apos;s permitted_emails or permitted_roles to confirm the
            final visibility looks right.
          </p>
        </>
      )}
    </>
  );
}

function VisibilityRow({ granted, primary, secondary, reason, testid }) {
  return (
    <div
      data-testid={testid}
      data-granted={granted ? "true" : "false"}
      className="flex items-start gap-3 py-2.5"
    >
      <div className="flex-shrink-0 pt-0.5">
        {granted ? (
          <CheckCircle size={16} weight="fill" className="text-emerald-600" />
        ) : (
          <XCircle size={16} weight="fill" className="text-slate-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-sm font-medium text-slate-900">{primary}</div>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              granted ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
            }`}
          >
            {granted ? "Granted" : "Denied"}
          </span>
        </div>
        {secondary && (
          <div className="mt-0.5 text-[11px] text-slate-500">{secondary}</div>
        )}
        <div className="mt-1 text-xs text-slate-600">
          <span className="font-medium text-slate-500">Reason:</span> {reason}
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, suffix, tone, testid }) {
  const toneClass =
    tone === "emerald" ? "border-emerald-200 bg-emerald-50" :
    tone === "slate"   ? "border-slate-200 bg-slate-50" :
                          "border-slate-200 bg-white";
  return (
    <div
      data-testid={testid}
      className={`rounded-md border ${toneClass} p-3`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <div className="text-2xl font-semibold text-slate-900">{value}</div>
        {suffix && <div className="text-xs text-slate-500">{suffix}</div>}
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  const tone = value === "Active"
    ? "bg-emerald-50 text-emerald-700"
    : "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {value || "—"}
    </span>
  );
}

function ChipMuted({ children }) {
  return (
    <span className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
      {children}
    </span>
  );
}


// ==========================================================================
// Preview As User — simulated Dashboard view
// ==========================================================================
// Renders what the selected user would actually see: their widgets in slot
// order, their permitted applications as tiles, and a permissions summary.
// This is NOT impersonation — no session change, no auth swap. Purely a
// read-through of the audit payload the current admin already loaded.
function PreviewAsUserDialog({ audit, onClose }) {
  const grantedWidgets = audit.widgets.filter((w) => w.granted);
  const grantedApps = audit.applications.filter((a) => a.granted);

  // Group widgets by their slot so the preview mirrors real Dashboard layout.
  const bySlot = {};
  grantedWidgets.forEach((w) => {
    (bySlot[w.slot] = bySlot[w.slot] || []).push(w);
  });
  const slotOrder = ["hero", "row-1", "row-2", "row-3", "footer"];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="visibility-audit-preview-dialog"
        className="max-h-[92vh] max-w-4xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserFocus size={18} weight="bold" />
            Preview Dashboard as {audit.user.full_name}
          </DialogTitle>
          <DialogDescription>
            Simulation only · you remain signed in as yourself.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
          <div className="flex items-center gap-1.5 font-medium">
            <Warning size={14} weight="bold" /> Preview Mode
          </div>
          <div className="mt-0.5">
            This view mirrors what <b>{audit.user.email}</b> would see after
            signing in. No session is switched; permissions in the app are
            unchanged.
          </div>
        </div>

        {/* Identity strip */}
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {initials(audit.user.full_name)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">
                {audit.user.full_name}
              </div>
              <div className="truncate font-mono text-[11px] text-slate-500">
                {audit.user.email}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <ChipMuted>{audit.user.role}</ChipMuted>
              <StatusPill value={audit.user.status} />
            </div>
          </div>
        </div>

        {/* Widgets */}
        <div className="mt-4">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <SquaresFour size={12} weight="bold" /> Dashboard Widgets
          </div>
          {grantedWidgets.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
              No widgets available for this user.
            </div>
          ) : (
            <div className="space-y-3">
              {slotOrder.map((slot) => {
                const items = bySlot[slot];
                if (!items || items.length === 0) return null;
                return (
                  <div key={slot}>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {slot}
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {items.map((w) => (
                        <div
                          key={w.key}
                          data-testid={`preview-widget-${w.key}`}
                          className="rounded-md border border-slate-200 bg-white p-3"
                        >
                          <div className="text-sm font-medium text-slate-900">{w.title}</div>
                          <div className="mt-0.5 text-[11px] text-slate-500">{w.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Applications */}
        <div className="mt-4">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <AppWindow size={12} weight="bold" /> My Applications
          </div>
          {grantedApps.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
              No applications visible to this user.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
              {grantedApps.map((a) => (
                <div
                  key={a.id}
                  data-testid={`preview-app-${a.id}`}
                  className="rounded-md border border-slate-200 bg-white p-3"
                >
                  <div className="text-sm font-semibold text-slate-900">{a.name}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <ChipMuted>{a.status}</ChipMuted>
                    {a.deployment && <ChipMuted>{a.deployment}</ChipMuted>}
                  </div>
                  {a.description && (
                    <p className="mt-2 line-clamp-2 text-[11px] text-slate-500">{a.description}</p>
                  )}
                  <div className="mt-2 text-[10px] text-slate-400">{a.reason}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Permissions summary */}
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Permissions summary
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <PermChip label="Widgets" value={`${audit.summary.widgets_granted} / ${audit.summary.widgets_total}`} />
            <PermChip label="Apps" value={`${audit.summary.applications_granted} / ${audit.summary.applications_total}`} />
            <PermChip label="Hidden" value={audit.summary.applications_hidden} />
            <PermChip label="Role" value={audit.user.role} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PermChip({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}


// ==========================================================================
// Coming-soon placeholder — used by all not-yet-implemented modules
// ==========================================================================
function ComingSoonModule({ module: mod }) {
  return (
    <ModuleShell mod={mod}>
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
        <Clock size={26} weight="light" className="mx-auto text-slate-400" />
        <div className="mt-2 text-sm font-medium text-slate-700">
          {mod.label} diagnostics — coming in a future release
        </div>
        <div className="mx-auto mt-1 max-w-md text-[13px] text-slate-500">
          {mod.description || "This module will surface health probes, recent activity, and error rates for this subsystem."}
        </div>
      </div>
    </ModuleShell>
  );
}


// ==========================================================================
// Shared UI primitives
// ==========================================================================
function ModuleShell({ mod, onReload, children }) {
  const Icon = mod.icon;
  return (
    <div data-testid={`diag-module-panel-${mod.key}`} className="rounded-md border border-slate-200 bg-white p-4">
      <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <Icon size={18} weight="regular" className="text-slate-500" />
          <h3 className="text-base font-semibold text-slate-900">{mod.label}</h3>
        </div>
        {onReload && (
          <button
            data-testid={`diag-module-reload-${mod.key}`}
            onClick={onReload}
            className="rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            Reload
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function SectionCard({ title, children, spacedTop }) {
  return (
    <div className={spacedTop ? "mt-4" : ""}>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        {children}
      </div>
    </div>
  );
}

function MetaGrid({ children }) {
  return <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">{children}</dl>;
}

function MetaLine({ label, value, mono, muted, children }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd
        className={`text-sm ${mono ? "font-mono" : ""} ${muted ? "text-slate-500" : "text-slate-800"} truncate`}
        title={typeof value === "string" ? value : undefined}
      >
        {children || value || "—"}
      </dd>
    </div>
  );
}

function formatIso(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-CA", { hour12: false });
}


// ==========================================================================
// Module Registry (add new modules by dropping an entry here)
// ==========================================================================
const MODULES = [
  { key: "system-info", label: "System Information", icon: Info, component: SystemInfoModule,
    description: "Build, version, environment, and third-party connectivity." },
  { key: "delivery-profile", label: "Delivery Profile (rpt_004)", icon: TruckTrailer, component: DeliveryProfileDiagModule,
    description: "Probe a customer's delivery profile visibility for the current user." },
  { key: "customer-lookup", label: "Customer Lookup", icon: MagnifyingGlass, component: ComingSoonModule, comingSoon: true,
    description: "Search the customer master by account/name and inspect returned data + resolved user permissions." },
  { key: "microsoft-entra", label: "Microsoft Entra Authentication", icon: ShieldCheck, component: ComingSoonModule, comingSoon: true,
    description: "Live token validation, claim inspection, tenant/audience mismatch detection." },
  { key: "microsoft-graph", label: "Microsoft Graph Email", icon: EnvelopeSimple, component: ComingSoonModule, comingSoon: true,
    description: "Client-credentials probe, permission verification, and recent send/receive audit tail." },
  { key: "notifications", label: "Notification Engine", icon: BellRinging, component: ComingSoonModule, comingSoon: true,
    description: "Outbound queue length, failure rate, and last-N notifications." },
  { key: "task-engine", label: "Task Engine", icon: ClipboardText, component: ComingSoonModule, comingSoon: true,
    description: "Task-type routing, assignment rules, and SLA counters." },
  { key: "database", label: "Database Connectivity", icon: HardDrives, component: ComingSoonModule, comingSoon: true,
    description: "Ping, replica-set status, index health, slow-query sample." },
  { key: "object-storage", label: "Object Storage", icon: Cloud, component: ComingSoonModule, comingSoon: true,
    description: "Bucket accessibility, last-upload timestamp, quota (if reported)." },
  { key: "samsara", label: "Samsara Integration", icon: Truck, component: ComingSoonModule, comingSoon: true,
    description: "Live vehicle count, poller lag, last successful sync." },
  { key: "visibility-audit", label: "Permission Audit Suite", icon: UserFocus, component: PermissionAuditSuite,
    description: "By-user, compare, role, and application audits. Pre-flight validation before onboarding." },
  { key: "application-launcher", label: "Application Launcher", icon: RocketLaunch, component: ComingSoonModule, comingSoon: true,
    description: "Registry state, launch-method routing tests, launcher session logs." },
  { key: "system-health", label: "System Health", icon: Heartbeat, component: ComingSoonModule, comingSoon: true,
    description: "Rollup of everything above with a single green/amber/red badge." },
];
